import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import Button from "@/components/ui/Button.jsx";
import SettingsScreenShell from "@/sot/layouts/SettingsScreenShell.jsx";
import TextField56 from "@/sot/primitives/TextField56.jsx";
import TopBarCenter from "@/sot/primitives/TopBarCenter.jsx";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import PasswordRequirementChecklist from "@/features/security/PasswordRequirementChecklist.jsx";
import {
  PASSWORD_RULES_ATTR,
  generateCompliantPassword,
  meetsPasswordPolicy,
} from "@/features/security/passwordPolicy.js";
import supabase, {
  getSupabaseAnonKey,
  getSupabaseBaseUrl,
} from "@/services/supabaseClient.js";
import {
  checkStagingAccess,
  normalizeStagingEmail,
  writeCachedStagingAccess,
} from "@/services/stagingAccessService.js";

const EHR_RESET_STAGING_HANDOFF_KEY = "afrimed:ehr-reset:staging-handoff:v1";

function PasswordField({
  label,
  value,
  onChange,
  autoComplete,
  disabled = false,
}) {
  const { t } = useLanguage();
  const [visible, setVisible] = React.useState(false);

  return (
    <div className="relative">
      <TextField56
        label={label}
        value={value}
        disabled={disabled}
        onChange={onChange}
        autoComplete={autoComplete}
        type={visible ? "text" : "password"}
        tone="surface"
        className="pr-10"
        inputProps={{
          minLength: 8,
          passwordrules: PASSWORD_RULES_ATTR,
        }}
      />
      <button
        type="button"
        onClick={() => setVisible((next) => !next)}
        aria-label={
          visible
            ? t("settings.password.hidePassword", "Hide password")
            : t("settings.password.showPassword", "Show password")
        }
        className="absolute bottom-3 right-3 flex h-7 w-7 items-center justify-center"
      >
        <img
          src={visible ? "/icons/list-items/visibility.svg" : "/icons/list-items/visibility_off.svg"}
          alt=""
          className="h-5 w-5"
        />
      </button>
    </div>
  );
}

export default function EhrPasswordResetConfirm() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();
  const auth = useAuth() || {};
  const token = String(searchParams.get("token") || "").trim();
  const [newPassword, setNewPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [resetEmail, setResetEmail] = React.useState("");
  const [error, setError] = React.useState("");
  const functionUrl = `${getSupabaseBaseUrl()}/functions/v1/ehr-password-reset`;

  function writeResetStagingHandoff(email = "") {
    const normalizedEmail = normalizeStagingEmail(email);
    if (!normalizedEmail) return;
    writeCachedStagingAccess(normalizedEmail);
    try {
      sessionStorage.setItem(
        EHR_RESET_STAGING_HANDOFF_KEY,
        JSON.stringify({
          email: normalizedEmail,
          grantedAt: Date.now(),
        }),
      );
    } catch {
      // ignore storage failures
    }
  }

  async function refreshStagingAccess(email = "") {
    const normalizedEmail = normalizeStagingEmail(email);
    if (!normalizedEmail) return "";
    writeResetStagingHandoff(normalizedEmail);
    try {
      const result = await checkStagingAccess(normalizedEmail);
      if (result.allowed) writeResetStagingHandoff(result.email);
    } catch {
      // The reset-success handoff should still use the verified reset email.
    }
    return normalizedEmail;
  }

  async function confirmResetPassword() {
    const payload = JSON.stringify({ token, password: newPassword });
    const response = await fetch(functionUrl, {
      method: "POST",
      mode: "cors",
      headers: {
        "Content-Type": "application/json",
        apikey: getSupabaseAnonKey(),
      },
      body: payload,
    });
    const text = await response.text().catch(() => "");
    let body = null;
    try {
      body = text ? JSON.parse(text) : null;
    } catch {
      body = null;
    }

    if (!response.ok) {
      const nextError = new Error(body?.message || body?.error || text || `HTTP ${response.status}`);
      nextError.status = response.status;
      nextError.body = body;
      throw nextError;
    }

    return body || { ok: true };
  }

  async function waitForPersistedSession() {
    for (let attempt = 0; attempt < 8; attempt += 1) {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (session?.access_token && session?.user?.id) return session;
      await new Promise((resolve) => window.setTimeout(resolve, 150));
    }
    return null;
  }

  async function handleSuccessHandoff(resetEmail = "") {
    const email =
      normalizeStagingEmail(auth?.user?.email) ||
      normalizeStagingEmail(resetEmail);
    setResetEmail(email);
    if (!email) {
      setSaved(true);
      return;
    }

    await refreshStagingAccess(email);
    const { error: signInError } = await supabase.auth.signInWithPassword({
      email,
      password: newPassword,
    });

    if (signInError) {
      setSaved(true);
      return;
    }

    const session = await waitForPersistedSession();
    if (!session?.access_token) {
      setSaved(true);
      return;
    }

    writeResetStagingHandoff(email);
    await auth?.refreshProfile?.();
    window.location.assign("/settings/patient/p/ehr?ehrPassword=ready");
  }

  async function handleBackToEhr() {
    const email =
      normalizeStagingEmail(auth?.user?.email) ||
      normalizeStagingEmail(resetEmail);
    if (email) {
      await refreshStagingAccess(email);
      if (newPassword) {
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email,
          password: newPassword,
        });
        if (!signInError) {
          await waitForPersistedSession();
          await auth?.refreshProfile?.();
        }
      }
      writeResetStagingHandoff(email);
    }
    window.location.assign("/settings/patient/p/ehr?ehrPassword=ready");
  }

  function handleGeneratePassword() {
    const generated = generateCompliantPassword();
    setNewPassword(generated);
    setConfirmPassword(generated);
    setError("");
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (submitting) return;

    if (!token) {
      setError(t("settings.password.reset.confirm.missingToken", "This reset link is missing or invalid."));
      return;
    }

    if (newPassword.length < 8) {
      setError(
        t(
          "settings.password.reset.confirm.minLength",
          "Use at least 8 characters with uppercase, lowercase, number and symbol.",
        ),
      );
      return;
    }

    if (!meetsPasswordPolicy(newPassword)) {
      setError(
        t(
          "settings.password.reset.confirm.policy",
          "Use at least 8 characters with uppercase, lowercase, number and symbol.",
        ),
      );
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(t("settings.password.reset.confirm.mismatch", "Passwords do not match. Try again."));
      return;
    }

    setSubmitting(true);
    setError("");
    try {
      const resetResult = await confirmResetPassword();
      await handleSuccessHandoff(resetResult?.email);
    } catch (nextError) {
      setError(
        t(
          "settings.password.reset.confirm.saveError",
          "We couldn’t reset your health record password. Please request a new link and try again.",
        ),
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <SettingsScreenShell>
      <TopBarCenter close onBack={() => nav("/settings/patient/p/ehr")} />

      {saved ? (
        <div className="mt-5 space-y-6">
          <div className="space-y-2">
            <h1 className="text-headline-small text-on-surface">
              {t("settings.password.reset.confirm.successTitle", "Password saved")}
            </h1>
            <p className="text-body-medium text-on-surface-var">
              {t(
                "settings.password.reset.confirm.successSubtitle",
                "Your health record password has been reset.",
              )}
            </p>
          </div>

          <Button
            fullWidth
            onClick={handleBackToEhr}
          >
            {t("settings.password.reset.backToEhr", "Back to My Health Record")}
          </Button>
        </div>
      ) : (
      <form className="mt-5 space-y-6" onSubmit={handleSubmit}>
        <div className="space-y-2">
          <h1 className="text-headline-small text-on-surface">
            {t(
              "settings.password.reset.confirm.title",
              "Choose a new health record password",
            )}
          </h1>
          <p className="text-body-medium text-on-surface-var">
            {t(
              "settings.password.reset.confirm.subtitle",
              "Use at least 8 characters with uppercase, lowercase, number and symbol.",
            )}
          </p>
        </div>

        <div className="space-y-4">
          <PasswordField
            label={t("settings.password.reset.confirm.newLabel", "New password")}
            value={newPassword}
            onChange={setNewPassword}
            autoComplete="new-password"
            disabled={submitting}
          />
          <PasswordField
            label={t("settings.password.reset.confirm.confirmLabel", "Confirm password")}
            value={confirmPassword}
            onChange={setConfirmPassword}
            autoComplete="new-password"
            disabled={submitting}
          />
        </div>

        <PasswordRequirementChecklist password={newPassword} />
        <button
          type="button"
          className="text-body-medium text-primary"
          onClick={handleGeneratePassword}
          disabled={submitting}
        >
          {t("settings.password.generateAction", "Generate strong password")}
        </button>

        {error ? (
          <p className="text-body-small text-error">{error}</p>
        ) : null}

        <Button
          type="submit"
          fullWidth
          loading={submitting}
          disabled={!newPassword || !confirmPassword}
        >
          {t("settings.password.reset.confirm.action", "Save password")}
        </Button>
      </form>
      )}
    </SettingsScreenShell>
  );
}
